/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useCallback, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type { Conversation } from '../../types';
import { filterAndSortConversations } from '../../utils/conversation';
import { CloseChatButton } from '../button/close-chat-button';
import { FullScreenButton } from '../button/full-screen-button';
import { NewChatButton } from '../button/new-chat-button';
import { useConversationModal } from '../hooks/conversation-modal';
import { SearchInput } from '../search-input';

import { ChatItem } from './chat-item';
import { DeleteConversationModal } from './conversation-modal/delete-conversation-modal';
import { EditConversationModal } from './conversation-modal/edit-conversation-modal';
import { ConversationPaginationControls } from './conversation-pagination-controls';
import messages from './messages';

const styles = cssMap({
	wrapper: {
		overflowX: 'hidden',
		height: '100%',
		width: '100%',
	},
	recentChat: {
		overflow: 'hidden',
	},
	conversationList: {
		overflowY: 'auto',
		paddingInline: token('space.150'),
		listStyle: 'none',
		marginTop: token('space.0'),
	},
	searchBar: {
		position: 'sticky',
		paddingTop: token('space.100'),
		paddingBottom: token('space.200'),
		paddingInline: token('space.150'),
	},
	accessibleSearchResults: {
		opacity: 0,
		position: 'absolute',
	},
	chatItem: {
		marginTop: token('space.0'),
	},
});

type ChatConversationListProps = {
	conversations: Conversation[];
	isFullScreen: boolean;
	currentConversationId: string;
	onSelectConversation: (conversationId: string) => void;
	onNewConversation: () => void;
	/** If not provided, the close chat button will not be shown */
	onCloseChat?: () => void;
	/** If not provided, the maximize chat button will not be shown */
	onMaximizeChat?: () => void;
	onConfirmEditConversation: (
		conversationId: string,
		newName: string,
	) => Promise<void | {
		error: string;
	}>;
	onConfirmDeleteConversation: (conversationId: string) => Promise<void | {
		error: string;
	}>;
	hasMoreConversations?: boolean;
	isLoadingMoreConversations?: boolean;
	hasLoadMoreConversationsError?: boolean;
	onLoadMoreConversations?: () => void;
	/** replaces the current history panel header with this component */
	chatRefreshHeader?: React.ReactNode;
};

export const ChatConversationList = ({
	conversations,
	currentConversationId,
	onSelectConversation,
	onNewConversation,
	onCloseChat,
	onMaximizeChat,
	isFullScreen,
	onConfirmEditConversation,
	onConfirmDeleteConversation,
	hasMoreConversations,
	isLoadingMoreConversations,
	hasLoadMoreConversationsError,
	onLoadMoreConversations,
	chatRefreshHeader,
}: ChatConversationListProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [searchValue, setSearchValue] = useState('');
	const { conversation } = useConversationModal();
	const isPinnedChatsEnabled = fg('rovo_chat_pinned_fe');

	const sortedConversations = useMemo(
		() =>
			filterAndSortConversations(conversations, {
				direction: 'desc',
				searchValue,
				sortPinned: isPinnedChatsEnabled,
			}),
		[conversations, isPinnedChatsEnabled, searchValue],
	);

	const handleSearchChange = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
	}, []);
	const isSearchActive = searchValue.trim().length > 0;

	const header = !isFullScreen ? (
		<Box padding="space.200">
			<Inline alignBlock="center" spread="space-between">
				<Inline alignBlock="center" space="space.050">
					<Heading as="h2" size="small">
						{formatMessage(messages.headerTitle)}
					</Heading>
				</Inline>

				<Inline space="space.050">
					<NewChatButton
						onClick={(_, e) => {
							sendAnalyticsEvent(
								e.update({
									actionSubjectId: 'chatViewNewChatButton',
								}),
							);
							onNewConversation();
						}}
					/>
					{!!onMaximizeChat && (
						<FullScreenButton
							onClick={(_, e) => {
								sendAnalyticsEvent(
									e.update({
										actionSubjectId: 'maximizeChatButton',
									}),
								);
								onMaximizeChat();
							}}
						/>
					)}
					{!!onCloseChat && (
						<CloseChatButton
							onClick={(_, e) => {
								sendAnalyticsEvent(
									e.update({
										actionSubjectId: 'chatViewCloseChatButton',
									}),
								);
								onCloseChat();
							}}
						/>
					)}
				</Inline>
			</Inline>
		</Box>
	) : (
		<Box paddingBlockEnd="space.300" paddingInline="space.200" paddingBlockStart="space.200">
			<Inline alignBlock="center" spread="space-between">
				<Heading size="medium">{formatMessage(messages.headerTitle)}</Heading>

				{!!onCloseChat && (
					<CloseChatButton
						onClick={(_, e) => {
							sendAnalyticsEvent(
								e.update({
									actionSubjectId: 'chatViewCloseChatButton',
								}),
							);
							onCloseChat();
						}}
					/>
				)}
			</Inline>
		</Box>
	);

	return (
		<Stack xcss={styles.wrapper} testId="ai-mate-conversation-list-view" alignBlock="start">
			{chatRefreshHeader ? chatRefreshHeader : header}
			<Box xcss={styles.searchBar}>
				<SearchInput
					placeholderText={formatMessage(messages.searchPlaceholder)}
					onSearch={handleSearchChange}
				/>
			</Box>

			<Stack space="space.150" xcss={styles.recentChat}>
				{/* For screen readers to announce updated search results */}
				<Box xcss={styles.accessibleSearchResults} role="status" aria-atomic>
					{formatMessage(messages.accessibleSearchResults, {
						results: sortedConversations.length,
					})}
				</Box>
				<Stack xcss={styles.conversationList} as="ul">
					{sortedConversations.length === 0 && (
						<Box paddingBlockStart="space.200" as="li" xcss={styles.chatItem}>
							<Text align="center" color="color.text.subtle">
								{formatMessage(messages.noResults)}
							</Text>
						</Box>
					)}
					{sortedConversations.length > 0 &&
						sortedConversations.map((conversation) => (
							<Box key={conversation.id} as="li" xcss={styles.chatItem}>
								<ChatItem
									conversation={conversation}
									isSelected={currentConversationId === conversation.id}
									onSelectConversation={onSelectConversation}
									isFullScreen={isFullScreen}
								/>
							</Box>
						))}
					{!isSearchActive && (
						<ConversationPaginationControls
							hasMoreConversations={hasMoreConversations}
							isLoadingMoreConversations={isLoadingMoreConversations}
							hasLoadMoreConversationsError={hasLoadMoreConversationsError}
							onLoadMoreConversations={onLoadMoreConversations}
							testIdPrefix="chat-conversation-list"
						/>
					)}
				</Stack>
			</Stack>

			<EditConversationModal
				onEditConfirm={(name) => onConfirmEditConversation(conversation.id, name)}
			/>

			<DeleteConversationModal
				onDeleteConfirm={() => onConfirmDeleteConversation(conversation.id)}
			/>
		</Stack>
	);
};
